import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Drawer,
  Empty,
  Input,
  Tree,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  FolderOpenOutlined,
  FolderOutlined,
  SettingOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import { getProjectEquipmentBucketOverrideTree } from '../state/projects.actions';
import useToggle from '../../common/hooks/useToggle';
import ProjectAddFromBucketSettingsModal from './ProjectAddFromBucketSettingsModal';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import { getIdMap } from '../../helpers/helpers';
import { attachCustomDataToEquipment } from '../../equipment/Equipment';
import BorderlessButton from '../../common/buttons/BorderlessButton';

const { DirectoryTree } = Tree;

const searchTreeRecursively = (searchTerm, nodes) => {
  const results = [];
  nodes.forEach((node) => {
    const matchesSearch = node.name?.toLowerCase().includes(searchTerm.toLowerCase());
    if (node.children?.length) {
      const matchingChildren = searchTreeRecursively(searchTerm, node.children);
      if (matchingChildren.length) {
        results.push({
          ...node,
          children: matchingChildren,
        });
      } else if (matchesSearch) {
        results.push({
          ...node,
          children: [],
        });
      }
    } else if (matchesSearch) {
      results.push(node);
    }
  });

  return results;
};

export default function ProjectAddFromBucketDrawer({
  visible,
  onClose,
  dataMap = {},
  onSave,
  divisionId,
  selectedEquipment,
}) {
  const dispatch = useDispatch();
  const { settings = {} } = useSelector((state) => state.settings.company);
  const projectEquipmentBucketOverrideTree = useSelector(
    (state) => state.projects.projectEquipmentBucketOverrideTree,
  );
  const statuses = useSelector((state) => state.equipment.statuses);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);

  const [expandedKeys, setExpandedKeys] = useState([]);
  const [drawerDataMap, setDrawerDataMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const { toggle, isToggled } = useToggle();

  useEffect(() => {
    if (visible) dispatch(getProjectEquipmentBucketOverrideTree());
  }, [visible]);

  useEffect(() => {
    if (!visible) setDrawerDataMap({});
    else setDrawerDataMap(dataMap);
  }, [visible, dataMap]);

  const equipmentTypeMap = useMemo(() => getIdMap(equipmentTypes), [equipmentTypes]);

  const shouldShowCheckbox = useMemo(() => {
    const { addFromBucket = {} } = settings;
    return !addFromBucket.equipmentType || !addFromBucket.mode || !addFromBucket.customField;
  }, [settings]);

  const onSubmit = useCallback(async () => {
    const equipmentToCreate = [];
    const statusArray = Object.values(statuses);

    Object.values(drawerDataMap).forEach((data) => {
      if (data.value) {
        const {
          addFromBucket: {
            equipmentType,
            customField,
          } = {},
        } = settings;

        const relevantType = equipmentTypeMap[equipmentType];
        if (shouldShowCheckbox) {
          equipmentToCreate.push({
            name: data.name,
            labels: data.labels,
            divisionIds: [divisionId],
            files: [],
            statusId: statusArray.find((s) => s.status === 'Active')?.id || statusArray[0]?.id || null,
            qrCodeValue: '',
            qrCodeFormat: 'qrCode',
            upstreamBucketIds: [data.id],
          });
          return;
        }

        const formattedEquipment = attachCustomDataToEquipment({
          equipment: {
            name: data.name,
            labels: data.labels,
            divisionIds: [divisionId],
            files: [],
            statusId: statusArray.find((s) => s.status === 'Active')?.id || statusArray[0]?.id || null,
            qrCodeValue: '',
            qrCodeFormat: 'qrCode',
            upstreamBucketIds: [data.id],
            customData: {
              [customField]: {
                value: data.value,
              },
            },
            equipmentTypeId: equipmentType,
          },
          customFields: relevantType?.customFields?.sections || [],
          ignoreErrors: true,
        });

        equipmentToCreate.push(formattedEquipment);
      }
    });
    onSave(equipmentToCreate);
    onClose();
  }, [
    drawerDataMap,
    divisionId,
    onSave,
    onClose,
    shouldShowCheckbox,
    equipmentTypeMap,
    statuses,
    settings,
  ]);

  const titleRender = useCallback((data) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', width: '80%' }}>
      <div style={{ width: '90%' }}>
        {data.name}
      </div>
      { !!(data.overrideName && data.isSelectable) && (
        <div style={{ position: 'absolute', top: 0, right: 0 }}>
          { shouldShowCheckbox
            ? (
              <OnTraccrCheckbox
                value={!!drawerDataMap[data.id]?.value}
                onChange={(value) => {
                  setDrawerDataMap((prev) => ({
                    ...prev,
                    [data.id]: {
                      ...prev[data.id],
                      name: data.overrideName,
                      labels: data.overrideLabel ? data.labels : [],
                      value: value ? 1 : 0,
                      id: data.id,
                    },
                  }));
                }}
              />
            ) : (
              <OnTraccrTextInput
                style={{ width: 150 }}
                size="small"
                min={0}
                onClick={(e) => {
                  // Prevent the click from propagating to the tree node
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onBlur={(e) => {
                  setDrawerDataMap((prev) => ({
                    ...prev,
                    [data.id]: {
                      name: data.overrideName,
                      labels: data.overrideLabel ? data.labels : [],
                      value: e?.target?.value,
                      id: data.id,
                    },
                  }));
                }}
                value={drawerDataMap[data.id]?.value}
              />
            )}
        </div>
      )}
    </div>
  ), [drawerDataMap, shouldShowCheckbox]);

  const getIcon = useCallback((node) => {
    if (node.isLeaf) return <ToolOutlined />;
    if (node.expanded) return <FolderOpenOutlined />;
    return <FolderOutlined />;
  }, []);

  const filteredTree = useMemo(() => {
    if (!searchTerm) return projectEquipmentBucketOverrideTree;
    return searchTreeRecursively(searchTerm, projectEquipmentBucketOverrideTree);
  }, [projectEquipmentBucketOverrideTree, searchTerm]);

  return (
    <Drawer
      title={(
        <div>
          <span>Add from Bucket</span>
          <BorderlessButton
            style={{ width: 30, marginRight: 30, marginBottom: 5 }}
            iconNode={<SettingOutlined />}
            onClick={toggle}
          />
        </div>
      )}
      width={720}
      onClose={onClose}
      visible={visible}
    >
      <Input.Search
        className="searchbar"
        placeholder="Search"
        onChange={(e) => setSearchTerm(e?.target?.value)}
        allowClear
        width={50}
        style={{
          width: '100%',
          marginBottom: 10,
        }}
      />
      <div
        style={{
          overflowY: 'scroll',
          height: 'calc(100% - 65px)',
        }}
      >
        <DirectoryTree
          treeData={filteredTree}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys)}
          titleRender={titleRender}
          icon={getIcon}
          rootStyle={{ height: '100%' }}
        />
        {!filteredTree?.length && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
      <ProjectAddFromBucketSettingsModal
        visible={isToggled}
        onClose={toggle}
      />
    </Drawer>
  );
}

ProjectAddFromBucketDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  divisionId: PropTypes.string.isRequired,
  dataMap: PropTypes.shape({}),
  selectedEquipment: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })),
};

ProjectAddFromBucketDrawer.defaultProps = {
  dataMap: {},
  selectedEquipment: [],
};
